import { useState } from 'react'
import { useNavigate } from 'react-router'
import api from '../lib/api'

const STEPS = [
  'Datos básicos',
  'Base de datos',
  'Credenciales MH',
  'Certificado',
  'Emisor',
  'Activar',
]

const INIT = {
  company_name:     '',
  nit:              '',
  subdomain:        '',
  ambiente:         '00',
  db_host:          '',
  db_port:          3306,
  db_database:      '',
  db_username:      '',
  db_password:      '',
  api_user:         '',
  api_password:     '',
  password_privado: '',
  admin_name:       '',
  admin_email:      '',
  admin_password:   '',
}

export default function Wizard() {
  const navigate = useNavigate()
  const [step,       setStep]       = useState(0)
  const [form,       setForm]       = useState(INIT)
  const [connResult, setConnResult] = useState(null)
  const [testing,    setTesting]    = useState(false)
  const [provisioning, setProvisioning] = useState(false)
  const [errors,     setErrors]     = useState({})

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  const testDb = async () => {
    setTesting(true)
    setConnResult(null)
    try {
      const { data } = await api.post('/wizard/test-db', {
        db_host:     form.db_host,
        db_port:     form.db_port,
        db_database: form.db_database,
        db_username: form.db_username,
        db_password: form.db_password,
      })
      setConnResult({ ok: data.success, msg: data.message })
    } catch (e) {
      setConnResult({ ok: false, msg: e.response?.data?.message ?? 'No se pudo conectar.' })
    } finally {
      setTesting(false)
    }
  }

  const provision = async () => {
    setProvisioning(true)
    try {
      const { data } = await api.post('/wizard/provision', form)
      if (data.success) {
        navigate(`/empresas/${data.tenant_id}`)
      }
    } catch (e) {
      setErrors({ provision: e.response?.data?.message ?? 'Error durante el provisionamiento.' })
    } finally {
      setProvisioning(false)
    }
  }

  const canAdvanceStep = () => {
    if (step === 0) return form.company_name && form.nit && form.subdomain
    if (step === 1) return connResult?.ok
    return true
  }

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span className="crumb" onClick={() => navigate('/empresas')}>Empresas</span>
          <span className="sep">›</span>
          <strong>Nuevo cliente</strong>
        </div>
      </div>

      <div className="content" style={{ maxWidth: '800px' }}>
        {/* Wizard steps indicator */}
        <div className="wizard-steps">
          {STEPS.map((label, i) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div className={`w-num ${i < step ? 'done' : i === step ? 'active' : ''}`}>
                  {i < step ? '✓' : i + 1}
                </div>
                <div className={`w-label${i === step ? ' active' : ''}`}>{label}</div>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-line${i < step ? ' done' : ''}`} />
              )}
            </div>
          ))}
        </div>

        {/* ── Paso 0: Datos básicos ── */}
        {step === 0 && (
          <div className="panel">
            <div className="panel-head" style={{ color: 'var(--accent)' }}>Paso 1 — Datos básicos del cliente</div>
            <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Nombre de la empresa</label>
                  <input className="form-input" placeholder="Distribuidora El Monte S.A." value={form.company_name} onChange={e => update('company_name', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">NIT</label>
                  <input className="form-input mono" placeholder="0614-120345-001-1" value={form.nit} onChange={e => update('nit', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Subdominio</label>
                  <input className="form-input mono" placeholder="elmonte" value={form.subdomain} onChange={e => update('subdomain', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} />
                  {form.subdomain && <span style={{ fontSize: '11px', color: 'var(--muted)' }}>{form.subdomain}.efactura.sv</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Ambiente</label>
                  <select className="form-input" value={form.ambiente} onChange={e => update('ambiente', e.target.value)}>
                    <option value="00">Pruebas (00)</option>
                    <option value="01">Producción (01)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Paso 1: Base de datos ── */}
        {step === 1 && (
          <div className="panel" style={{ borderColor: 'var(--accent)' }}>
            <div className="panel-head" style={{ color: 'var(--accent)' }}>Paso 2 — Configurar base de datos propia</div>
            <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div className="info-banner">
                Cada tenant tiene su propia BD aislada. Configura la conexión al servidor MariaDB donde se alojará este cliente.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Host del servidor</label>
                  <input className="form-input mono" placeholder="db1.internal.efactura.sv" value={form.db_host} onChange={e => update('db_host', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Puerto</label>
                  <input className="form-input mono" type="number" value={form.db_port} onChange={e => update('db_port', parseInt(e.target.value))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Nombre de la BD</label>
                  <input className="form-input mono" placeholder={`efac_${form.subdomain || 'cliente'}`} value={form.db_database} onChange={e => update('db_database', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Usuario BD</label>
                  <input className="form-input mono" placeholder={`efac_${form.subdomain || 'cliente'}_usr`} value={form.db_username} onChange={e => update('db_username', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Contraseña BD</label>
                  <input className="form-input" type="password" placeholder="Contraseña segura…" value={form.db_password} onChange={e => update('db_password', e.target.value)} />
                </div>
              </div>

              {connResult && (
                <div className={`conn-result ${connResult.ok ? 'ok' : 'err'}`}>
                  {connResult.ok ? '✓' : '✕'} {connResult.msg}
                </div>
              )}

              <button className="btn btn-success" onClick={testDb} disabled={testing || !form.db_host || !form.db_database}>
                {testing ? '⏳ Probando conexión…' : '⚡ Probar conexión'}
              </button>
            </div>
          </div>
        )}

        {/* ── Paso 2: Credenciales MH ── */}
        {step === 2 && (
          <div className="panel">
            <div className="panel-head" style={{ color: 'var(--accent)' }}>Paso 3 — Credenciales API Ministerio de Hacienda</div>
            <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div className="form-group"><label className="form-label">Usuario API MH</label><input className="form-input mono" placeholder="NIT_PROD_2026" value={form.api_user} onChange={e => update('api_user', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Contraseña API MH</label><input className="form-input" type="password" value={form.api_password} onChange={e => update('api_password', e.target.value)} /></div>
            </div>
          </div>
        )}

        {/* ── Paso 3: Certificado ── */}
        {step === 3 && (
          <div className="panel">
            <div className="panel-head" style={{ color: 'var(--accent)' }}>Paso 4 — Certificado digital</div>
            <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">Contraseña del certificado (.p12)</label>
                <input className="form-input" type="password" placeholder="Contraseña del archivo .p12" value={form.password_privado} onChange={e => update('password_privado', e.target.value)} />
              </div>
              <div className="info-banner">El archivo .p12 se sube directamente al servidor firmador. Aquí solo se guarda la contraseña para que el firmador pueda usarlo.</div>
            </div>
          </div>
        )}

        {/* ── Paso 4: Emisor ── */}
        {step === 4 && (
          <div className="panel">
            <div className="panel-head" style={{ color: 'var(--accent)' }}>Paso 5 — Datos del emisor</div>
            <div className="panel-body">
              <div className="info-banner">El emisor se configura dentro de la aplicación del tenant una vez activado. Puedes continuar.</div>
            </div>
          </div>
        )}

        {/* ── Paso 5: Activar ── */}
        {step === 5 && (
          <div className="panel" style={{ borderColor: 'var(--green)' }}>
            <div className="panel-head" style={{ color: 'var(--green)' }}>Paso 6 — Confirmar y provisionar</div>
            <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

              {/* Resumen */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                <div><span style={{ color: 'var(--muted)' }}>Empresa:</span> <strong>{form.company_name}</strong></div>
                <div><span style={{ color: 'var(--muted)' }}>NIT:</span> <span className="mono">{form.nit}</span></div>
                <div><span style={{ color: 'var(--muted)' }}>Subdominio:</span> <span className="mono">{form.subdomain}.efactura.sv</span></div>
                <div><span style={{ color: 'var(--muted)' }}>BD:</span> <span className="mono">{form.db_database}@{form.db_host}</span></div>
                <div><span style={{ color: 'var(--muted)' }}>Ambiente:</span> {form.ambiente === '01' ? 'Producción' : 'Pruebas'}</div>
              </div>

              {/* Usuario administrador inicial */}
              <div>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
                  Usuario administrador del tenant
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Nombre</label>
                    <input
                      className="form-input"
                      placeholder="Administrador"
                      value={form.admin_name}
                      onChange={e => update('admin_name', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Correo electrónico</label>
                    <input
                      className="form-input"
                      type="email"
                      placeholder="admin@empresa.com"
                      value={form.admin_email}
                      onChange={e => update('admin_email', e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Contraseña inicial</label>
                    <input
                      className="form-input"
                      type="password"
                      placeholder="Mínimo 8 caracteres"
                      value={form.admin_password}
                      onChange={e => update('admin_password', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {errors.provision && <div className="error-banner">⚠ {errors.provision}</div>}

              <div className="info-banner">
                Al confirmar se creará el registro del tenant en la BD central y se ejecutarán las migraciones en <strong>{form.db_database}</strong>. El tenant quedará inactivo hasta que lo actives manualmente.
              </div>

              <button
                className="btn btn-primary"
                onClick={provision}
                disabled={provisioning || !form.admin_name || !form.admin_email || form.admin_password.length < 8}
                style={{ justifyContent: 'center' }}
              >
                {provisioning ? '⏳ Provisionando…' : '🚀 Crear y provisionar tenant'}
              </button>
            </div>
          </div>
        )}

        {/* Navegación */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
          <button className="btn btn-ghost" onClick={() => step === 0 ? navigate('/empresas') : setStep(s => s - 1)}>
            {step === 0 ? '← Cancelar' : '← Anterior'}
          </button>
          {step < STEPS.length - 1 && (
            <button
              className="btn btn-primary"
              onClick={() => setStep(s => s + 1)}
              disabled={!canAdvanceStep()}
            >
              Continuar →
            </button>
          )}
        </div>
      </div>
    </>
  )
}
